// TextComposer.tsx · v1.0 · v12.10.7.1 (#1177 Send text from a contact) · 2026-10-05 · VoiceSpectrum AI
//
// The Send text window, drawn with the SAME markup and classes as the Messages composer (#793 / #805 / #806:
// aw-text-composer, To as a link with "Change number", the count on its own line, Cancel · Send text with Ctrl+Enter,
// secondary while empty and primary once there is text). Contacts uses it; Messages keeps its inline copy for this
// hotfix (its composer also carries the templates and the EN / ES switch, which Contacts does not have yet - Tushar
// 10/5: "here there will be no templates"). Moving Messages onto this component is a later clean-up, logged in the
// build log, not done four days before go-live.
import { useEffect, useRef, useState } from 'react';
import { smsSegments } from '../lib/smsText';

function fmtNational(d: string): string {
  const x = d.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return x.length === 10 ? `(${x.slice(0, 3)}) ${x.slice(3, 6)}-${x.slice(6)}` : x;
}

export default function TextComposer({ initialTo, note, consentLine, sending, error, onCancel, onSend }: {
  initialTo: string;                 // E.164 or digits
  note: string;                      // the line under the buttons - where the text is logged
  consentLine?: React.ReactNode;     // e.g. "Text consent: given"
  sending: boolean;
  error?: string | null;
  onCancel: () => void;
  onSend: (to: string, body: string) => void;
}) {
  const [to, setTo] = useState(() => initialTo.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, ''));
  const [toEditing, setToEditing] = useState(false);
  const [body, setBody] = useState('');
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { window.setTimeout(() => area.current?.focus(), 0); }, []);
  const send = () => { if (body.trim() && !sending) onSend(to, body.trim()); };
  const segs = smsSegments(body);
  return (
    <section className="aw-section-card aw-editor aw-text-composer" aria-label="Send text message"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); e.stopPropagation(); send(); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
      }}>
      <div className="aw-tc-h">
        <b>Send text message</b>
        <span className="aw-tc-to">
          To{' '}
          {toEditing ? (
            <span className="aw-to-edit">
              <span className="aw-cc" aria-label="Country code">+1</span>
              <input className="aw-to-input" type="tel" inputMode="tel" value={to} aria-label="Phone number"
                onChange={(e) => setTo(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setToEditing(false); } }} />
              <button className="aw-to-ok" type="button" onClick={() => setToEditing(false)}>Done</button>
            </span>
          ) : (
            <button className="aw-to-link" type="button" aria-label="Change number" title="Click to change the number" onClick={() => setToEditing(true)}>
              +1 {fmtNational(to) || 'Add a number'}
            </button>
          )}
          {consentLine && <>{' · '}{consentLine}</>}
        </span>
      </div>
      <textarea ref={area} rows={4} value={body} placeholder="Type a message to send..." aria-label="Message to send" onChange={(e) => setBody(e.target.value)} />
      {error && <div className="aw-tc-error" role="alert">{error}</div>}
      <div className="aw-tc-countrow">
        <span className={`aw-tc-count${body.length > 160 ? ' aw-over' : ''}`} aria-live="polite">
          <b>{body.length}</b> / 160 characters {'·'} {segs} message{segs === 1 ? '' : 's'}
          {body.length > 160 && ` — will send as ${segs} texts`}
        </span>
      </div>
      <div className="aw-tc-foot">
        <button className="aw-secondary" type="button" onClick={onCancel}>Cancel</button>
        <button className={body.trim() ? 'aw-primary' : 'aw-secondary'} type="button" disabled={!body.trim() || sending} onClick={send}>
          {sending ? 'Sending...' : 'Send text'} <span className="aw-kbd">Ctrl+{'↵'}</span>
        </button>
      </div>
      <div className="aw-tc-note"><span>{note}</span></div>
    </section>
  );
}
